import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import SelectSingle from '@ui/select/react-select/select-single';
import { ICONS } from '@ui/icons/types';
import type { ResolvedEmailBlock } from '../../../templates/widgets/block-types';
import ColorPill from '../controls/color-pill';
import NumberField from '../controls/number-field';
import BoxModelControl from '../controls/box-model-control';
import { useBoxModelStyle } from '../controls/use-box-model-style';
import { useResolvedDividerStyle } from '../controls/use-resolved-divider-style';

const LINE_STYLE_OPTIONS = [
  { label: 'Solid', value: 'solid' },
  { label: 'Dashed', value: 'dashed' },
  { label: 'Dotted', value: 'dotted' },
];

interface DividerStylePanelProps {
  block: ResolvedEmailBlock;
  onUpdateStyle: (key: string, value: unknown) => void;
}

export default function DividerStylePanel({ block, onUpdateStyle }: DividerStylePanelProps) {
  const style = block.style ?? {};
  const resolved = useResolvedDividerStyle(block.id);

  const padding = useBoxModelStyle({
    blockId: block.id,
    styleValue: style.padding,
    resolvedValues: [resolved.paddingTop, resolved.paddingRight, resolved.paddingBottom, resolved.paddingLeft],
    styleKey: 'padding',
    uniformity: 'paired',
    onUpdateStyle,
  });

  return (
    <Container className="flex-column gap-16" padding="0 16px">
      <ColorPill
        label="Background color"
        value={style.background as string | undefined}
        isOverridden={style.background !== undefined}
        onChange={(value) => onUpdateStyle('background', value)}
        onReset={() => onUpdateStyle('background', undefined)}
      />

      <Container className="flex gap-8 ai-flex-end">
        <ColorPill
          label="Line color"
          value={(style.color as string | undefined) ?? resolved.lineColor ?? '#000000'}
          isOverridden={style.color !== undefined}
          onChange={(value) => onUpdateStyle('color', value)}
          onReset={() => onUpdateStyle('color', undefined)}
        />
        <Container className="flex-column gap-2 flex-1" minWidth="0">
          <Text tag="label" design="GlobalIcoText3" className="L2 padding-l-16">
            Line style
          </Text>
          <SelectSingle
            value={(style.lineStyle as string | undefined) ?? resolved.lineStyle ?? 'solid'}
            options={LINE_STYLE_OPTIONS}
            placeholder="Solid"
            width="100%"
            menuPortal={false}
            onChange={(value) => onUpdateStyle('lineStyle', value)}
          />
        </Container>
      </Container>

      <BoxModelControl
        sectionLabel="Padding"
        expanded={padding.expanded}
        onToggleExpand={() => padding.setExpanded((prev) => !prev)}
        toggleIcon={ICONS.PADDING_SIDES}
        toggleLabel="padding controls"
        values={padding.values}
        onChangeSlot={padding.setSlot}
        fields={[
          { slot: 0, icon: ICONS.PADDING_TOP, ariaLabel: 'Padding top' },
          { slot: 1, icon: ICONS.PADDING_RIGHT, ariaLabel: 'Padding right' },
          { slot: 2, icon: ICONS.PADDING_BOTTOM, ariaLabel: 'Padding bottom' },
          { slot: 3, icon: ICONS.PADDING_LEFT, ariaLabel: 'Padding left' },
        ]}
        renderCollapsed={() => (
          <Container className="flex gap-12 ai-flex-end">
            <NumberField
              label="Padding"
              icon={ICONS.PADDING_VERTICAL}
              allowNegative={false}
              min={0}
              max={100}
              value={padding.values[0]}
              onChange={padding.setCollapsedFirst}
            />
            <NumberField
              icon={ICONS.PADDING_HORIZONTAL}
              allowNegative={false}
              min={0}
              max={100}
              value={padding.values[1]}
              onChange={padding.setCollapsedSecond}
            />
          </Container>
        )}
      />
    </Container>
  );
}
